"use client";

import { cn } from "@/lib/utils";
import {
    AnimatePresence,
    HTMLMotionProps,
    motion,
    useMotionValue,
    useSpring
} from "framer-motion";
import { useEffect, useRef, useState } from "react";

// Array of fun emojis to randomly select from
const EMOJIS = [
    "🎁",
    "🎄",
    "🎅",
    "❄️",
    "⛄",
    "🦌",
    "🎄",
    "🎊",
    "🎉",
    "✨",
    "🌟",
    "💫",
    "🎈",
    "🎀",
    "🎁",
    "🎄",
    "🎅",
    "🤶",
    "🧑‍🎄",
    "🎄",
    "🎁",
    "🎄",
    "🎅",
    "❄️",
    "⛄",
    "🦌",
    "🎄",
    "🎊",
    "🎉",
    "✨"
];

/**
 * A custom pointer component that displays an animated cursor.
 * Add this as a child to any component to enable a custom pointer when hovering.
 * You can pass custom children to render as the pointer.
 *
 * @component
 * @param {HTMLMotionProps<"div">} props - The component props
 */
export function AnimatedCursor({
    className,
    style,
    children,
    ...props
}: HTMLMotionProps<"div">): React.ReactNode {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    // Create spring-based motion values for smooth, velocity-based transitions
    const springConfig = { damping: 20, stiffness: 300, mass: 0.5 };
    const springX = useSpring(x, springConfig);
    const springY = useSpring(y, springConfig);
    const [isActive, setIsActive] = useState<boolean>(false);
    const [isEnabled, setIsEnabled] = useState<boolean>(false);
    const [selectedEmoji, setSelectedEmoji] = useState<string>("🎁");
    const containerRef = useRef<HTMLDivElement>(null);

    // Check if cursor should be enabled based on localStorage
    const checkCursorEnabled = () => {
        if (typeof window !== "undefined") {
            const cursorValue = localStorage.getItem("mix_christmas_cursor");
            // Check if the value is a valid emoji from our list
            const isValidEmoji = cursorValue && EMOJIS.includes(cursorValue);
            setIsEnabled(!!isValidEmoji);
            // If valid emoji, use it directly
            if (isValidEmoji && cursorValue) {
                setSelectedEmoji(cursorValue);
            }
        }
    };

    useEffect(() => {
        // Initial check
        checkCursorEnabled();

        // Listen for storage changes (cross-tab)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "mix_christmas_cursor") {
                checkCursorEnabled();
            }
        };

        // Listen for custom event (same-tab)
        const handleCustomStorageChange = () => {
            checkCursorEnabled();
        };

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener(
            "mix_christmas_cursor_changed",
            handleCustomStorageChange
        );

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(
                "mix_christmas_cursor_changed",
                handleCustomStorageChange
            );
        };
    }, []);

    useEffect(() => {
        // Only enable cursor if localStorage value is a valid emoji
        if (!isEnabled) {
            return;
        }

        if (typeof window !== "undefined") {
            // Add event listeners to document
            const handleMouseMove = (e: MouseEvent) => {
                x.set(e.clientX);
                y.set(e.clientY);
                setIsActive(true);
            };

            const handleMouseLeave = () => {
                setIsActive(false);
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseleave", handleMouseLeave);

            // Set initial position and active state
            setIsActive(true);

            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseleave", handleMouseLeave);
            };
        }
    }, [x, y, isEnabled]);

    return (
        <>
            <div ref={containerRef} />
            <AnimatePresence>
                {isEnabled && isActive && (
                    <motion.div
                        className="pointer-events-none fixed z-[9999]"
                        style={{
                            top: springY,
                            left: springX,
                            transform: "translate(10px, 10px)",
                            ...style
                        }}
                        initial={{
                            scale: 0,
                            opacity: 0
                        }}
                        animate={{
                            scale: 1,
                            opacity: 1
                        }}
                        exit={{
                            scale: 0,
                            opacity: 0
                        }}
                        transition={{
                            scale: {
                                type: "spring",
                                damping: 15,
                                stiffness: 300
                            },
                            opacity: {
                                duration: 0.2
                            }
                        }}
                        {...props}
                    >
                        {children || (
                            <div
                                className={cn(
                                    "select-none text-2xl",
                                    className
                                )}
                            >
                                {selectedEmoji}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
